import { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";

interface Payment {
  id: string; order_id: string; order_no: string; company_name: string;
  method: "card" | "bank_transfer" | "tax_invoice"; status: "pending" | "completed" | "failed" | "cancelled";
  amount: number; provider: string | null; provider_tid: string | null;
  paid_at: number | null; created_at: number;
}
const fmt = (n: number) => n.toLocaleString("ko-KR");
const METHOD: Record<string, string> = { card: "카드", bank_transfer: "계좌이체", tax_invoice: "세금계산서" };
const STATUS: Record<string, { text: string; cls: string }> = {
  pending:   { text: "대기", cls: "badge-warning" },
  completed: { text: "완료", cls: "badge-success" },
  failed:    { text: "실패", cls: "badge-danger" },
  cancelled: { text: "취소", cls: "badge-muted" },
};

export default function AdminPayments() {
  const [items, setItems] = useState<Payment[]>([]);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState<string>("all");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const qs = filter === "all" ? "?limit=50" : `?status=${filter}&limit=50`;
    api.get<{ items: Payment[]; total: number }>(`/admin/payments${qs}`)
      .then((r) => { setItems(r.items); setTotal(r.total); })
      .finally(() => setLoading(false));
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const qs = filter === "all" ? "" : `&status=${filter}`;
      const r = await api.get<{ items: Payment[]; total: number }>(`/admin/payments?limit=50&offset=${items.length}${qs}`);
      setItems((prev) => [...prev, ...r.items]);
      setTotal(r.total);
    } finally { setLoadingMore(false); }
  };

  const confirm = async (p: Payment) => {
    if (!window.confirm(`${p.order_no} ₩${fmt(p.amount)} 결제를 완료 처리할까요?`)) return;
    await api.post(`/admin/payments/${p.id}/confirm`);
    load();
  };
  const cancel = async (p: Payment) => {
    if (!window.confirm(`${p.order_no} 결제를 취소할까요?`)) return;
    await api.post(`/admin/payments/${p.id}/cancel`);
    load();
  };

  return (
    <>
      <div className="row" style={{ marginBottom: 16, gap: 8, flexWrap: "wrap" }}>
        <h2 className="h-section" style={{ margin: 0 }}>결제 {items.length}건</h2>
        <div className="grow" />
        {(["all", "pending", "completed", "failed", "cancelled"] as const).map((f) => (
          <button key={f} className={`btn btn-${filter === f ? "primary" : "outline"} btn-sm`} onClick={() => setFilter(f)}>
            {f === "all" ? "전체" : STATUS[f]?.text ?? f}
          </button>
        ))}
      </div>

      <div className="card" style={{ overflowX: "auto" }}>
        <table>
          <thead><tr><th>일시</th><th>주문번호</th><th>업체</th><th>결제 수단</th><th>상태</th><th style={{ textAlign: "right" }}>금액</th><th>거래 ID</th><th></th></tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} style={{ padding: 40, textAlign: "center" }} className="muted">로딩 중...</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={8} style={{ padding: 40, textAlign: "center" }} className="muted">결제 내역이 없습니다.</td></tr>
            ) : items.map((p) => {
              const s = STATUS[p.status];
              return (
                <tr key={p.id}>
                  <td style={{ fontSize: 15 }} className="muted">{new Date(p.created_at * 1000).toLocaleString("ko-KR")}</td>
                  <td className="mono" style={{ fontSize: 15, fontWeight: 600 }}>{p.order_no}</td>
                  <td>{p.company_name}</td>
                  <td style={{ fontSize: 15 }}>{METHOD[p.method] ?? p.method}</td>
                  <td><span className={`badge ${s.cls}`}>{s.text}</span></td>
                  <td style={{ textAlign: "right", fontWeight: 600 }}>{fmt(p.amount)}원</td>
                  <td className="mono" style={{ fontSize: 15 }}>{p.provider_tid ?? "—"}</td>
                  <td style={{ textAlign: "right" }}>
                    {p.status === "pending" && (
                      <>
                        <button className="btn btn-primary btn-sm" onClick={() => confirm(p)}>입금 확인</button>
                        <button className="btn btn-ghost btn-sm" onClick={() => cancel(p)} style={{ color: "var(--danger)" }}>취소</button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {!loading && items.length < total && (
        <div style={{ textAlign: "center", marginTop: 16 }}>
          <button className="btn btn-outline btn-sm" onClick={loadMore} disabled={loadingMore}>
            {loadingMore ? "불러오는 중..." : `더 보기 (${items.length}/${total})`}
          </button>
        </div>
      )}
      <p className="muted" style={{ fontSize: 15, marginTop: 12 }}>
        ※ PG 미계약 상태에서는 모든 결제가 <strong>수동 입금 확인</strong> 방식입니다. 토스페이먼츠 계약 후 자동 승인/웹훅이 활성화됩니다.
      </p>
    </>
  );
}
